import React, { useEffect, useState, useRef } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import ReactSearchBox from "react-search-box";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getRestaurantDetails } from "../services/UserLogin.service";
import { useDispatch, useSelector } from "react-redux";
import {
  removePreLoginCartItem,
  restaurantDetailsAction,
  setCategories,
  userCartItems,
  userCartDetails,
  logoutSession,
} from "../redux/actions/Actions";
import {
  getSubCategory,
  getSearchProducts,
  getProductsByColorSize,
  getAllProducts,
  filterProducts,
} from "../services/Products.service";
import { getCartItems, deleteCartItem, cartAvailability, getActiveOffers, isCouponEnabledByAdmin } from "../services/Cart.service";
import { PATH, RESTAURANT_ID } from "../redux/constants/Constants";
import { getAllCoupons, getOrderList } from "../services/Order.service";
import FloatingCoupon from "./FloatingCoupon";
import WhatsAppButton from "./WhatsAppButton";
import WelcomeAuthModal from "./WelcomeAuthModal";
import FloatingBackButton from "./FloatingBackButton";
import { sendSubscriptionEmail } from "../utils/emailService";

const ImageWithLoader = ({ src, alt, className, style, width, height, ...props }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const fallbackSrc = `${PATH}/assets/img/bamboosa/bamboosa-logo.png`;
  const imgRef = React.useRef(null);

  React.useEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      setLoading(false);
    }
  }, [src]);

  const containerStyle = {
    position: "relative",
    display: style?.display || "inline-block",
    width: style?.width || (width ? `${width}px` : undefined),
    height: style?.height || (height ? `${height}px` : undefined),
    aspectRatio: (width && height && style?.width !== "auto") ? `${width}/${height}` : undefined,
    ...style,
  };

  return (
    <div style={containerStyle}>
      {loading && !error && (
        <div style={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1
        }}>
          <div className="spinner-border spinner-border-sm text-success" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
      <img
        ref={imgRef}
        src={error ? fallbackSrc : (src || fallbackSrc)}
        alt={alt || "Bamboosa image"}
        className={className}
        width={width}
        height={height}
        style={{
          width: "100%",
          height: "100%",
          objectFit: style?.objectFit || "contain",
          opacity: loading ? 0 : 1,
          transition: "opacity 0.2s"
        }}
        onLoad={() => setLoading(false)}
        onError={() => { setLoading(false); setError(true); }}
        {...props}
      />
    </div>
  );
};

export default function Site() {
  const [restaurantDetails, setRestaurantDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [placeholderText, setPlaceholderText] = useState("");
  const [menSubCategory, setMenSubCategory] = useState([]);
  const [womenSubCategory, setWomenSubCategory] = useState([]);
  const [cartVisible, setCartVisible] = useState(false);
  const [accountVisible, setAccountVisible] = useState(false);
  const cartItemList = useSelector(
    (state) => state?.cartItems?.cartItems?.results
  );
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [menMenuVisible, setMenMenuVisible] = useState(false);
  const [womenMenuVisible, setWomenMenuVisible] = useState(false);
  const [clickVisible, setClickVisible] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [productSuggestions, setProductSuggestions] = useState(null);
  const [searchBoxVisible, setSearchBoxVisible] = useState(false);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const placeholder = "Search For";
  const token = useSelector((state) => state.status?.restaurant?.Token);

  const userData = useSelector((state) => state.user?.user?.token);
  const cartId = useSelector(
    (state) => state?.cart?.cart?.results?.[0]?.id
  );
  const cartCount = useSelector((state) => state?.cartItems?.cartItems?.count);
  const totalCartQuantity = Array.isArray(cartItemList) ? cartItemList.reduce((acc, item) => acc + (Number(item?.quantity) || 0), 0) : 0;

  const currencySymbol = useSelector(
    (state) => state.status?.restaurant?.currency_symbol
  );
  const node = useRef();
  const logo = useSelector((state) => state.status?.restaurant?.compony_image);
  const preLoginCount = useSelector(
    (state) => state?.preLoginCart?.preLoginCart?.length
  );
  const preLoginCart = useSelector(
    (state) => state?.preLoginCart?.preLoginCart
  );
  const totalPreLoginQuantity = Array.isArray(preLoginCart) ? preLoginCart.reduce((acc, item) => acc + (Number(item?.quantity) || 0), 0) : 0;

  const [isBouncing, setIsBouncing] = useState(false);
  const prevCartCount = useRef(cartCount);
  const prevPreLoginCount = useRef(preLoginCount);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMessage, setAuthModalMessage] = useState('');

  useEffect(() => {
    if ((totalCartQuantity || 0) === 0 && (totalPreLoginQuantity || 0) === 0) {
      setCartVisible(false);
    }
    if (totalCartQuantity > prevCartCount.current || totalPreLoginQuantity > prevPreLoginCount.current) {
      setIsBouncing(true);
      const timer = setTimeout(() => setIsBouncing(false), 500);
      return () => clearTimeout(timer);
    }
    prevCartCount.current = totalCartQuantity;
    prevPreLoginCount.current = totalPreLoginQuantity;
  }, [totalCartQuantity, totalPreLoginQuantity]);

  // Track Omnisend page views on route changes for React SPA
  useEffect(() => {
    if (window.omnisend) {
      window.omnisend.push(["track", "$pageViewed"]);
    }
  }, [location]);

  const aboutUs = useSelector((state) => state.status?.restaurant?.about_us);
  const address = useSelector((state) => state.status?.restaurant?.address);
  const city = useSelector((state) => state.status?.restaurant?.city);
  const state = useSelector((state) => state.status?.restaurant?.state);
  const country = useSelector((state) => state.status?.restaurant?.country);
  const zip = useSelector((state) => state.status?.restaurant?.zip);
  const businessName = useSelector((state) => state.status?.restaurant?.business_name);
  const email = useSelector((state) => state.status?.restaurant?.support_email);
  const phone = useSelector(
    (state) => state.status?.restaurant?.support_phone_number
  );
  const working_hours = useSelector(
    (state) => state.status?.restaurant?.working_hours
  );

  const [hasOrders, setHasOrders] = useState(false);

  useEffect(() => {
    if (token && userData?.id) {
      getOrderList(token, userData.id)
        .then((res) => {
          if (res?.data?.results && res.data.results.length > 0) {
            setHasOrders(true);
          } else {
            setHasOrders(false);
          }
        })
        .catch((err) => {
          setHasOrders(false);
        });
    } else {
      setHasOrders(false);
    }
  }, [token, userData]);

  useEffect(() => {
    if (token && userData?.id) {
      cartAvailability(token, userData.id)
        .then((res) => {
          dispatch(userCartDetails(res?.data));
          const cId = res?.data?.results?.[0]?.id;
          if (cId) {
            getCartItems(token, cId).then((itemsRes) => {
              dispatch(userCartItems(itemsRes?.data));
            }).catch(() => {
            });
          }
        })
        .catch((err) => {
          console.warn("Cart availability check failed:", err?.message);
        });
    }
  }, [token, userData]);

  const newAddress =
    address + ", " + city + ", " + state + ", " + country + ", " + zip;

  const cachedCategories = useSelector((state) => state?.categories?.categories);

  useEffect(() => {
    getRestaurantDetails().then((res) => {
      if (res?.data) {
        setRestaurantDetails(res?.data);
        dispatch({ type: "RESTAURANT_DETAILS", payload: res?.data });

        if (cachedCategories) {
          setMenSubCategory(cachedCategories.men);
          setWomenSubCategory(cachedCategories.women);
        } else {
          const localCache = localStorage.getItem("categories");
          if (localCache) {
            const parsed = JSON.parse(localCache);
            setMenSubCategory(parsed.men);
            setWomenSubCategory(parsed.women);
          } else {
            getSubCategory(res?.data.Token).then((res) => {
              const men = [];
              const women = [];

              res?.data?.forEach((subCategory) => {
                const categoryFor = subCategory?.category_for
                  ?.toLowerCase()
                  .trim();

                if (/^men'?s?$/.test(categoryFor)) {
                  men.push(subCategory);
                } else if (/^women'?s?$/.test(categoryFor)) {
                  women.push(subCategory);
                }
              });

              setMenSubCategory(men);
              setWomenSubCategory(women);

              const payload = { men, women };
            });
          }
        }
      }
    });

    if (cartVisible || mobileMenuVisible || searchBoxVisible) {
      document.addEventListener("click", handleOutsideClick, false);
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
      if (mobileMenuVisible) {
        document.body.style.position = "fixed";
        document.body.style.width = "100%";
        document.body.style.top = `-${window.scrollY}px`;
      }
    } else {
      document.removeEventListener("click", handleOutsideClick, false);
      const scrollY = document.body.style.top;
      document.body.style.overflow = "auto";
      document.documentElement.style.overflow = "auto";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    return () => {
      document.removeEventListener("click", handleOutsideClick, false);
      const scrollY = document.body.style.top;
      document.body.style.overflow = "auto";
      document.documentElement.style.overflow = "auto";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    };
  }, [cartVisible, mobileMenuVisible, searchBoxVisible, cachedCategories]);

  useEffect(() => {
    if (location.pathname === `/search`) {
      setInputValue(inputValue);
    } else {
      setInputValue("");
    }
  }, [location.pathname]);



  const handleOutsideClick = (e) => {
    if (node.current && !node.current.contains(e.target)) {
      setCartVisible(false);
      setInputValue("");
      setSearchBoxVisible(false);
    }
  };

  const handleItemDelete = (cart_item_id) => {
    deleteCartItem(token, cart_item_id).then(() => {
      cartId &&
        getCartItems(token, cartId).then((res) => {
          dispatch(userCartItems(res?.data));
        });
    });
  };

  const removePreLoginCartItemhandle = (color_id) => {
    dispatch(removePreLoginCartItem(color_id));
  };

  const handleMobileMenu = () => {
    setMobileMenuVisible((prevVisible) => !prevVisible);
    setMenMenuVisible(false);
    setWomenMenuVisible(false);
  };

  const handleMenMenu = () => {
    setMenMenuVisible((prevVisible) => !prevVisible);
    setWomenMenuVisible(false);
  };

  const handleWomenMenu = () => {
    setWomenMenuVisible((prevVisible) => !prevVisible);
    setMenMenuVisible(false);
  };

  const clickOnMenu = () => {
    setMobileMenuVisible((prevVisible) => !prevVisible);
    setMenMenuVisible(false);
    setWomenMenuVisible(false);
  };

  const toggleCart = () => {
    setCartVisible((prevVisible) => !prevVisible);
  };

  const handleSearchChange = (e) => {
    const searchKey = e?.target?.value;
    setInputValue(searchKey);
    
    if (searchKey && searchKey.length >= 2) {
      Promise.allSettled([
        getSearchProducts(token, searchKey),
        filterProducts(token, { color: searchKey, size: "", minPrice: "", maxPrice: "" })
      ]).then(async (results) => {
        let mergedProducts = [];
        
        // Process GraphQL search results (by name)
        if (results[0].status === 'fulfilled' && results[0].value) {
          const gqlResults = results[0].value?.data?.data?.productSearch || [];
          const formattedGql = gqlResults.map(r => ({
             productId: r.productId || r.product_id,
             productName: r.productName || r.product_name,
             productUrl: r.productUrl || r.product_url,
             image_url: r.image || r.image_url || r.imageUrl || r.product_image,
             category: r.category,
             productObj: r
          }));
          mergedProducts = [...formattedGql];
        }
        
        // Process REST API filter results (by color)
        if (results[1].status === 'fulfilled' && results[1].value) {
          const restData = results[1].value?.data;
          const restResults = Array.isArray(restData) ? restData : (restData?.products || []);
          
          const variantPromises = restResults.map(async r => {
              try {
                  const varRes = await getProductsByColorSize(token, r.product_id);
                  const variants = varRes?.data?.color_obj_dict || [];
                  const matchedVariant = variants.find(v => {
                      const colorName = (v.color || v.color_name || v.color_id || '').toLowerCase();
                      return colorName.includes(searchKey.toLowerCase());
                  });
                  
                  if (matchedVariant && matchedVariant.image_url) {
                      return {
                          productId: String(r.product_id),
                          productName: `${matchedVariant.color.split(',')[0]} ${r.product_name}`,
                          productUrl: String(matchedVariant.image_url).split('@')[0],
                          image_url: String(matchedVariant.image_url).split('@')[0],
                          category: r.category,
                          matchedColor: matchedVariant.color.split(',')[0],
                          productObj: r
                      };
                  }
              } catch(e) {}
              
              return {
                 productId: String(r.product_id),
                 productName: r.product_name,
                 productUrl: r.product_url,
                 image_url: r.product_url,
                 category: r.category,
                 productObj: r
              };
          });
          
          const formattedRest = await Promise.all(variantPromises);
          
          // Merge without duplicates based on productId
          formattedRest.forEach(restProd => {
            if (!mergedProducts.some(p => String(p.productId) === restProd.productId)) {
              mergedProducts.push(restProd);
            }
          });
        }
        
        setFilteredSuggestions(mergedProducts);
        setSearchBoxVisible(true);
      }).catch(err => console.warn("Search API Error:", err?.message));
    } else {
      setProductSuggestions([]);
      setFilteredSuggestions([]);
      setSearchBoxVisible(false);
    }
  };

  const handleSearchProductSelect = async (suggestion) => {
    setSearchBoxVisible(false);
    setInputValue(suggestion?.productName);
    
    // Check if this is a generic base product without a specific color matched
    // If so, route it to the corresponding collection page (like the menu bar links)
    if (!suggestion.matchedColor) {
      const lowerName = suggestion?.productName?.toLowerCase() || "";
      if (lowerName.includes("women") && lowerName.includes("polo")) {
        navigate("/womens", { state: { initialType: "Polo Neck" } });
        return;
      } else if (lowerName.includes("women") && lowerName.includes("round")) {
        navigate("/womens", { state: { initialType: "Round Neck" } });
        return;
      } else if (lowerName.includes("men") && lowerName.includes("polo")) {
        navigate("/mens", { state: { initialType: "Polo Neck" } });
        return;
      } else if (lowerName.includes("men") && lowerName.includes("round")) {
        navigate("/mens", { state: { initialType: "Round Neck" } });
        return;
      }
    }

    try {
      // Fetch full product list to get the complete product object
      const allProdRes = await getAllProducts(token, "1");
      const fullProduct = allProdRes?.data?.results?.find(p => String(p.product_id) === String(suggestion.productId));
      const firstProductData = fullProduct || suggestion.productObj || { product_id: suggestion.productId, product_name: suggestion.productName };

      const colorRes = await getProductsByColorSize(token, suggestion.productId);
      const colorObj = colorRes?.data?.color_obj_dict || [];
      if (colorObj.length > 0) {
        let firstVariant = colorObj[0];
        if (suggestion.matchedColor) {
           const found = colorObj.find(c => c.color.toLowerCase().includes(suggestion.matchedColor.toLowerCase()));
           if (found) firstVariant = found;
        }
        
        navigate(`/product-details`, {
          state: {
            firstProductData: firstProductData,
            colorObj: colorObj,
            productsByColor: firstVariant,
            subCategory: suggestion.category || fullProduct?.category || [],
          }
        });
      } else {
        navigate(`/search`, { state: { subCategory: suggestion?.category } });
      }
    } catch (e) {
      navigate(`/search`, { state: { subCategory: suggestion?.category } });
    }
  };

  const handleViewCart = () => {
    setCartVisible(false);
  };

  const handleLogin = () => {
    toast.warning("Please sign in to access your cart.", {
      autoClose: 1500,
      style: { backgroundColor: "black", color: "white" },
      containerId: "site",
    });
    setTimeout(() => {
      navigate(`/login`);
    }, 3000);
  };

  const handleSearchClose = () => {
    setInputValue("");
    setSearchBoxVisible(false);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (document.activeElement) {
      document.activeElement.blur();
    }
    if (inputValue) {
      const lowerInput = inputValue.toLowerCase();
      
      // Navigate to correct collection pages for men/women polo/round searches
      if (lowerInput.includes("women") && lowerInput.includes("polo")) {
        navigate("/womens", { state: { initialType: "Polo Neck" } });
        setSearchBoxVisible(false);
        return;
      } else if (lowerInput.includes("women") && lowerInput.includes("round")) {
        navigate("/womens", { state: { initialType: "Round Neck" } });
        setSearchBoxVisible(false);
        return;
      } else if (lowerInput.includes("men") && lowerInput.includes("polo")) {
        navigate("/mens", { state: { initialType: "Polo Neck" } });
        setSearchBoxVisible(false);
        return;
      } else if (lowerInput.includes("men") && lowerInput.includes("round")) {
        navigate("/mens", { state: { initialType: "Round Neck" } });
        setSearchBoxVisible(false);
        return;
      }
      
      // If we have suggestions, navigate to the first one
      if (filteredSuggestions?.length > 0) {
        handleSearchProductSelect(filteredSuggestions[0]);
      } else if (inputValue.length >= 2) {
        // Try fetching suggestions if we don't have them yet
        handleSearchChange({ target: { value: inputValue } });
      }
    }
  };

  const [coupons, setCoupons] = useState();
  const [isCouponValid, setIsCouponValid] = useState(false);

  const fetchCoupons = (t) => {
    const authToken = t || token;
    if (!authToken) return;
    Promise.all([getAllCoupons(authToken), getActiveOffers(RESTAURANT_ID)])
      .then(([res, offersData]) => {
        if (res?.data && res.data.length > 0) {
          const sortedCoupons = res.data.sort((a, b) => b.id - a.id);
          const currentDate = new Date();
          currentDate.setHours(0, 0, 0, 0);
          
          const validCoupons = sortedCoupons.filter(coupon => {
            if (!coupon.expiry_date) return true;
            const expiry = new Date(coupon.expiry_date);
            expiry.setHours(0,0,0,0);
            return expiry >= currentDate;
          }).filter(coupon => isCouponEnabledByAdmin(coupon, offersData));

          if (validCoupons.length > 0) {
            setCoupons(validCoupons);
            setIsCouponValid(true);
          } else {
            setCoupons([]);
            setIsCouponValid(false);
          }
        } else {
          setCoupons([]);
          setIsCouponValid(false);
        }
      })
      .catch((err) => {
        console.error("Error fetching coupons in Site:", err);
        setCoupons([]);
        setIsCouponValid(false);
      });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (token) {
      fetchCoupons(token);
    } else {
      getRestaurantDetails()
        .then((res) => {
          if (res?.data?.Token) {
            fetchCoupons(res.data.Token);
          }
        })
        .catch((err) => console.log(err));
    }
  }, [token]);

  const handleProductDetails = async (item) => {
  const firstProductData = item.product;
  const colorRes = await getProductsByColorSize(token, item.product.product_id);
  const colorObj = colorRes?.data?.color_obj_dict || [];
  const productsByColor = item.color;
  const subCategory = [];

  navigate(`/product-details`, {
    state: {
      firstProductData,
      colorObj,
      productsByColor,
      subCategory,
    },
  });
};

  return (
    <div style={{ minHeight: "800px" }}>
      <ToastContainer
        position="top-center"
        limit={1}
        containerId="site"
      />

      <div style={{ backgroundColor: '#092615', color: '#fff', fontSize: '13px', padding: '8px 16px', width: '100%', overflow: 'hidden' }}>
        <div className="d-none d-md-flex justify-content-center align-items-center" style={{ maxWidth: '1400px', margin: '0 auto', gap: '40px' }}>
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            <span style={{ fontSize: '16px' }}>🚚</span> Free Shipping On Any Orders
          </div>
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            <span style={{ fontSize: '18px' }}>◯</span> Easy 7-Day Returns
          </div>
        </div>
        <div className="d-flex d-md-none align-items-center px-2">
          <marquee behavior="scroll" direction="left" scrollamount="4">
            <div className="d-flex align-items-center" style={{ gap: '30px', whiteSpace: 'nowrap', paddingLeft: '16px', paddingRight: '16px' }}>
              <span className="d-flex align-items-center" style={{ gap: '6px' }}><span style={{ fontSize: '14px' }}>🚚</span> Free Shipping on Orders</span>
              <span className="d-flex align-items-center" style={{ gap: '6px' }}><span style={{ fontSize: '16px' }}>◯</span> Easy 7-Day Returns</span>
            </div>
          </marquee>
        </div>
      </div>


      <header style={{ position: "sticky", top: 0, zIndex: 1020, backgroundColor: "#fff", width: "100%" }}>
        <div className="header-main sticky bdr-bottom" style={{ display: "flex", alignItems: "center", minHeight: "85px", padding: "14px 0", width: "100%", paddingRight: 0, marginRight: 0 }}>
          <div className="px-0 px-xl-3" style={{ maxWidth: "1400px", margin: "0 auto", width: "100%" }}>
            <div className="d-flex align-items-center justify-content-between w-100" style={{ flexWrap: "nowrap", whiteSpace: "nowrap", paddingRight: 0, marginRight: 0, gap: 0 }}>
              <div className="d-none d-xl-block logo-web position-static" style={{ flexShrink: 0 }}>
                <div className="logo icon-zoom-hover" style={{ display: "inline-block" }}>
                  <Link to={`/`} className="logo1">
                    <ImageWithLoader
                      className="logo-dimention"
                      src={logo || `${PATH}/assets/img/bamboosa/bamboosa-logo.png`}
                      alt="Brand logo"
                      style={{ height: "70px", width: "auto", objectFit: "contain", color: "transparent" }}
                    />
                  </Link>
                </div>
              </div>
              <div className="d-xl-none d-flex justify-content-between align-items-center position-static py-2 w-100 flex-grow-1" style={{ minHeight: "80px", padding: "0 0 0 12px" }}>
                <div className="logo icon-zoom-hover d-flex align-items-center">
                  <Link to={`/`} className="d-flex align-items-center">
                    <ImageWithLoader
                      src={logo || `${PATH}/assets/img/bamboosa/bamboosa-logo.png`}
                      alt="Brand logo"
                      style={{ height: "55px", width: "auto", objectFit: "contain", display: "block", color: "transparent" }}
                    />
                  </Link>
                </div>
                
                <div className="d-flex align-items-center" style={{ gap: "12px", marginLeft: "auto" }}>
                  {Array.isArray(userData) || !userData ? (
                    sessionStorage.getItem('has_registered') === 'true' ? (
                      <Link to={`/login`} className="d-flex align-items-center justify-content-center" style={{ textDecoration: "none", color: "inherit" }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                      </Link>
                    ) : (
                      <div onClick={() => setShowAuthModal(true)} className="d-flex align-items-center justify-content-center" style={{ cursor: "pointer" }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                      </div>
                    )
                  ) : (
                    <Link to={`/my-account`} className="d-flex align-items-center justify-content-center" style={{ textDecoration: "none", color: "inherit" }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                    </Link>
                  )}

                  <div 
                    onClick={() => {
                      if (!Array.isArray(userData) && userData) {
                        navigate('/cart');
                      } else {
                        setAuthModalMessage("Please sign in to access your cart");
                        setShowAuthModal(true);
                      }
                    }}
                    className="d-flex align-items-center justify-content-center"
                    style={{ 
                      background: "#f4f4f4", 
                      borderRadius: "20px", 
                      padding: "6px 12px",
                      cursor: "pointer",
                      gap: "6px"
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path><line x1="3" y1="6" x2="21" y2="6"></line><path d="M16 10a4 4 0 0 1-8 0"></path></svg>
                    <span style={{ fontSize: "14px", fontWeight: "600", color: "#111", marginBottom: "1px" }}>
                      {cartId ? totalCartQuantity : totalPreLoginQuantity}
                    </span>
                  </div>
                  
                  <div 
                    onClick={() => handleMobileMenu()}
                    className="d-flex flex-column align-items-center justify-content-center"
                    style={{
                      background: "#0b2e13",
                      borderRadius: "50%",
                      width: "36px",
                      height: "36px",
                      cursor: "pointer",
                      gap: mobileMenuVisible ? "0px" : "3px",
                      position: "relative"
                    }}
                  >
                    {mobileMenuVisible ? (
                      <>
                        <span style={{ width: "16px", height: "2px", background: "#fff", display: "block", position: "absolute", transform: "rotate(45deg)" }}></span>
                        <span style={{ width: "16px", height: "2px", background: "#fff", display: "block", position: "absolute", transform: "rotate(-45deg)" }}></span>
                      </>
                    ) : (
                      <>
                        <span style={{ width: "14px", height: "2px", background: "#fff", display: "block" }}></span>
                        <span style={{ width: "14px", height: "2px", background: "#fff", display: "block" }}></span>
                        <span style={{ width: "14px", height: "2px", background: "#fff", display: "block" }}></span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="d-none d-xl-flex align-items-center justify-content-between flex-grow-1" style={{ minWidth: 0, flexWrap: "nowrap", whiteSpace: "nowrap" }}>
                <div className="d-flex align-items-center flex-shrink-1" style={{ order: 2, margin: 0, flexWrap: "nowrap", minWidth: 0 }}>
                  <div className="header-setting-option d-flex justify-content-end align-items-center" style={{ gap: "15px", padding: 0, minWidth: 0, flexWrap: "nowrap" }}>
                    <div className="react-search-box web-search d-flex align-items-center" style={{ flex: "1 1 auto", minWidth: "100px", maxWidth: "200px", height: "40px", padding: "0 15px", borderRadius: "30px", backgroundColor: "#f5f5f6" }}>
                      <form onSubmit={handleSearchSubmit} className="search-wrap d-flex dropdown w-100 align-items-center" style={{ height: "100%", position: "relative" }}>
                        <button aria-label="Submit search" type="submit" className="search-trigger me-2 p-0 border-0 bg-transparent" style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#666" }}>
                          <i className="ion-ios-search-strong" style={{ fontSize: "18px" }}></i>
                          <span className="visually-hidden">Search</span>
                        </button>
                        <input
                          type="text"
                          placeholder="Search..."
                          className="form-control transparent-background p-0 m-0 border-0 shadow-none"
                          value={inputValue}
                          onChange={(e) => handleSearchChange(e)}
                          maxLength={50}
                          style={{ height: "100%", fontSize: "14px", backgroundColor: "transparent", width: "100%" }}
                        />

                        {inputValue ? (
                          <button
                            aria-label="Clear search"
                            type="button"
                            className="search-trigger ms-2 p-0 border-0 bg-transparent"
                            onClick={() => handleSearchClose()}
                            style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#666" }}
                          >
                            <i className="ion-ios-close" style={{ fontSize: "20px" }}></i>
                            <span className="visually-hidden">Clear</span>
                          </button>
                        ) : null}
                        {searchBoxVisible ? (
                          <div
                            className="dropdown-menu"
                            style={{
                              display: "block",
                              position: "absolute",
                              top: "100%",
                              left: 0,
                              width: "100%",
                              maxHeight: "300px",
                              overflowY: "auto",
                              zIndex: 1050,
                              marginTop: "5px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                              borderRadius: "8px",
                              padding: "0",
                              border: "none"
                            }}
                          >
                            <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                              {filteredSuggestions?.length > 0 ? (
                                filteredSuggestions.map((suggestion, index) => (
                                  <li
                                    key={index}
                                    onClick={() => handleSearchProductSelect(suggestion)}
                                    className="d-flex align-items-center dropdown-item"
                                  style={{ padding: "10px", cursor: "pointer", borderBottom: "1px solid #f0f0f0", whiteSpace: "normal" }}
                                >
                                  <img
                                    src={suggestion.productUrl || logo || `${PATH}/assets/img/bamboosa/bamboosa-logo.png`}
                                    alt="Product-Img"
                                    className="me-3"
                                    loading="lazy"
                                    style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px" }}
                                    onError={(e) => { e.target.onerror = null; e.target.src = logo || `${PATH}/assets/img/bamboosa/bamboosa-logo.png`; }}
                                  />
                                  <span style={{ fontSize: "14px", fontWeight: "500", color: "#333", lineHeight: "1.4" }}>{suggestion.productName}</span>
                                </li>
                              ))) : (
                                <li style={{ padding: "10px", textAlign: "center", color: "#666", fontSize: "14px" }}>
                                  No products found.
                                </li>
                              )}
                            </ul>
                          </div>
                        ) : null}
                      </form>
                    </div>
                    <div className="user-account">
                        <div 
                          className="user-icon d-flex align-items-center justify-content-center flex-column icon-zoom-hover" 
                          style={{ gap: "5px", position: "relative" }}
                          onMouseEnter={() => {
                            if (userData && !Array.isArray(userData)) {
                              setAccountVisible(true);
                            }
                          }}
                          onMouseLeave={() => setAccountVisible(false)}
                        >
                          {Array.isArray(userData) || !userData ? (
                            sessionStorage.getItem('has_registered') === 'true' ? (
                              <Link to={`/login`} className="d-flex flex-column align-items-center justify-content-center w-100 h-100" style={{ textDecoration: "none", color: "inherit", gap: "5px" }}>
                                <i className="fa fa-user-o" style={{ fontSize: "20px", color: "#333", marginBottom: "2px" }}></i>
                                <span style={{ fontSize: "13px", fontWeight: "600", marginTop: "0px", whiteSpace: "nowrap" }}>Sign In</span>
                              </Link>
                            ) : (
                              <div onClick={() => setShowAuthModal(true)} className="d-flex flex-column align-items-center justify-content-center w-100 h-100" style={{ cursor: "pointer", textDecoration: "none", color: "inherit", gap: "5px" }}>
                                <i className="fa fa-user-o" style={{ fontSize: "20px", color: "#333", marginBottom: "2px" }}></i>
                                <span style={{ fontSize: "13px", fontWeight: "600", marginTop: "0px", whiteSpace: "nowrap" }}>Sign In</span>
                              </div>
                            )
                          ) : (
                            <>
                              <Link to={`/my-account`} className="d-flex flex-column align-items-center justify-content-center w-100 h-100" style={{ textDecoration: "none", color: "inherit", gap: "5px", paddingBottom: "20px", marginBottom: "-20px" }}>
                                <i className="fa fa-user-o" style={{ fontSize: "20px", color: "#333", marginBottom: "2px" }}></i>
                                <span style={{ fontSize: "13px", fontWeight: "600", marginTop: "0px", whiteSpace: "nowrap" }}>Account</span>
                              </Link>
                              
                              {accountVisible && (
                                <div 
                                  style={{
                                    position: 'absolute',
                                    top: '100%',
                                    right: '-20px',
                                    paddingTop: '15px',
                                    zIndex: 9999,
                                    minWidth: '170px'
                                  }}
                                >
                                  <div
                                    className="shadow"
                                    style={{
                                      background: '#fff',
                                      borderRadius: '12px',
                                      border: '1px solid #f0f0f0',
                                      padding: '10px 0',
                                    }}
                                  >
                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, textAlign: 'left' }}>
                                      {localStorage.getItem("isGuest") !== "true" && (
                                        <li style={{ margin: 0, padding: 0 }}>
                                          <Link to="/my-account/profile" style={{ display: 'block', padding: '6px 20px', color: '#333', fontSize: '14px', fontWeight: '500', textDecoration: 'none', transition: 'color 0.2s', lineHeight: '1.4' }} onMouseOver={e => e.target.style.color = '#175d31'} onMouseOut={e => e.target.style.color = '#333'}>
                                            Profile
                                          </Link>
                                        </li>
                                      )}
                                      <li style={{ margin: 0, padding: 0 }}>
                                        <Link to="/my-account/orders" style={{ display: 'block', padding: '6px 20px', color: '#333', fontSize: '14px', fontWeight: '500', textDecoration: 'none', transition: 'color 0.2s', lineHeight: '1.4' }} onMouseOver={e => e.target.style.color = '#175d31'} onMouseOut={e => e.target.style.color = '#333'}>
                                          Orders
                                        </Link>
                                      </li>
                                      {localStorage.getItem("isGuest") !== "true" && (
                                        <li style={{ margin: 0, padding: 0 }}>
                                          <Link to="/my-account/addresses" style={{ display: 'block', padding: '6px 20px', color: '#333', fontSize: '14px', fontWeight: '500', textDecoration: 'none', transition: 'color 0.2s', lineHeight: '1.4' }} onMouseOver={e => e.target.style.color = '#175d31'} onMouseOut={e => e.target.style.color = '#333'}>
                                            My Addresses
                                          </Link>
                                        </li>
                                      )}
                                      {localStorage.getItem("isGuest") !== "true" && (
                                        <li style={{ margin: 0, padding: 0 }}>
                                          <Link to="/my-account/change-password" style={{ display: 'block', padding: '6px 20px', color: '#333', fontSize: '14px', fontWeight: '500', textDecoration: 'none', transition: 'color 0.2s', lineHeight: '1.4' }} onMouseOver={e => e.target.style.color = '#175d31'} onMouseOut={e => e.target.style.color = '#333'}>
                                            Change Password
                                          </Link>
                                        </li>
                                      )}
                                      <li style={{ borderTop: '1px solid #f0f0f0', margin: '4px 0 0 0', padding: 0 }}>
                                        <div 
                                          onClick={() => {
                                            dispatch(logoutSession());
                                            sessionStorage.removeItem("user_data");
                                            sessionStorage.removeItem("token");
                                            localStorage.removeItem("user_data");
                                            localStorage.removeItem("token");
                                            localStorage.removeItem("isGuest");
                                            window.location.href = "/";
                                          }} 
                                          style={{ display: 'block', padding: '8px 20px', color: '#e53e3e', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'color 0.2s', lineHeight: '1.4' }} 
                                          onMouseOver={e => e.target.style.color = '#c53030'} 
                                          onMouseOut={e => e.target.style.color = '#e53e3e'}
                                        >
                                          Logout
                                        </div>
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <div
                        className={`header-mini-cart ${cartVisible ? 'cart-hover-funnel' : ''}`}
                        ref={node}
                        onMouseEnter={() => {
                          if (totalCartQuantity > 0 || totalPreLoginQuantity > 0) {
                            setCartVisible(true);
                          }
                        }}
                        onMouseLeave={() => {
                          setCartVisible(false);
                        }}
                        style={{ paddingBottom: "40px", marginBottom: "-40px", marginLeft: "15px" }}
                      >
                        <div
                          className="mini-cart-btn d-flex flex-column align-items-center justify-content-center icon-zoom-hover"
                          style={{ cursor: "pointer", gap: "5px", position: "relative" }}
                          onClick={() => {
                            if (!Array.isArray(userData) && userData) {
                              navigate('/cart');
                            } else {
                              setAuthModalMessage("Please sign in to access your cart");
                              setShowAuthModal(true);
                            }
                            setCartVisible(false);
                          }}
                        >
                          <style>
                            {`
                              @keyframes cartBounce {
                                0% { transform: scale(1); }
                                50% { transform: scale(1.4) translateY(-5px); }
                                100% { transform: scale(1); }
                              }
                              .cart-bounce-animation {
                                animation: cartBounce 0.5s ease-in-out;
                              }
                              .icon-zoom-hover {
                                transition: transform 0.2s ease-in-out;
                              }
                              .icon-zoom-hover:hover {
                                transform: scale(1.15) !important;
                              }
                              .cart-hover-funnel::after {
                                content: "";
                                position: absolute;
                                top: 30px;
                                right: 0;
                                width: 360px;
                                height: 60px;
                                background: transparent;
                                z-index: 999999;
                              }
                            `}
                          </style>
                          <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <i className={`fa fa-shopping-bag ${isBouncing ? "cart-bounce-animation" : ""}`} style={{ fontSize: "20px", color: "#333" }}></i>
                            {(cartId ? totalCartQuantity : totalPreLoginQuantity) > 0 && (
                              <span className={isBouncing ? "cart-notification cart-bounce-animation" : "cart-notification"} style={{ position: "absolute", top: "-8px", right: "-8px", minWidth: "20px", height: "20px", display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px", fontSize: "11px", lineHeight: "1", borderRadius: "50%" }}>
                                {cartId ? totalCartQuantity : totalPreLoginQuantity}
                              </span>
                            )}
                          </div>
                          <span style={{ fontSize: "13px", fontWeight: "600", marginTop: "0px", whiteSpace: "nowrap" }}>Cart</span>
                        </div>
                        {cartVisible && (
                          <ul
                            className={
                              cartVisible
                                ? "cart-list scroll-bar d-block"
                                : "cart-list scroll-bar d-none"
                            }
                          >
                            {cartItemList?.map((item, i) =>
                              item.color?.quantity > 0 ? (
                                <li key={i} className="d-flex align-items-center mb-3 pb-2" style={{ borderBottom: "1px solid #f5f5f5" }}>
                                  <div className="cart-img" style={{ flex: "0 0 65px", maxWidth: "65px" }}>
                                    <Link to="javascript:void(0)">
                                      <img
                                        src={
                                          item.color?.image_url?.split("@")[0]
                                        }
                                        className="w-100 h-100"
                                        loading="lazy"
                                        alt="product-img"
                                      />
                                    </Link>
                                  </div>
                                  <div className="cart-info" style={{ flex: "1", paddingLeft: "12px", minWidth: 0 }}>
                                    <h4 style={{ fontSize: "12px", fontWeight: "600", marginBottom: "4px", lineHeight: "1.3", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                      <Link to="javascript:void(0)">
                                        {item.product.product_name} (
                                        {item.color?.color.split(",")[0]})
                                      </Link>
                                    </h4>
                                    <div className="d-flex justify-content-between align-items-center" style={{ fontSize: "11px", color: "#666" }}>
                                      <div className="size">
                                        Size: {item?.color?.size.name}
                                      </div>
                                      {item.color?.quantity > 0 ? (
                                        <div className="size">
                                          Qty.: {item?.quantity}
                                        </div>
                                      ) : (
                                        <div className="size">
                                          <strong style={{ color: "red", fontSize: "11px" }}>
                                            Out of Stock
                                          </strong>
                                        </div>
                                      )}
                                      {item.color?.quantity > 0 ? (
                                        <div className="size">
                                          {currencySymbol === "₹" ? "₹" : "$"}
                                          {(
                                            item.color?.price * 1
                                          ).toLocaleString("en-IN", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}
                                        </div>
                                      ) : null}
                                    </div>
                                    {item.color?.price &&
                                    item.color?.quantity > 0 ? (
                                      <span className="mt-0" style={{ fontSize: "13px", fontWeight: "600", color: "#175d31", display: "block", marginTop: "4px" }}>
                                        {currencySymbol === "₹" ? "₹" : "$"}
                                        {(
                                          item.color?.price * item?.quantity
                                        ).toLocaleString("en-IN", {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}
                                      </span>
                                    ) : null}
                                  </div>
                                  <div
                                    className="del-icon"
                                    style={{ flex: "0 0 24px", textAlign: "right", marginLeft: "5px" }}
                                    onClick={() =>
                                      handleItemDelete(item.cart_item_id)
                                    }
                                  >
                                    <i
                                      className="fa fa-trash"
                                      style={{ color: "#175d31", fontSize: "16px" }}
                                    ></i>
                                  </div>
                                </li>
                              ) : null
                            )}
                            {cartItemList?.map((item, i) =>
                              item.color?.quantity < 1 ? (
                                <li key={i} className="d-flex align-items-center mb-3 pb-2" style={{ borderBottom: "1px solid #f5f5f5" }}>
                                  <div className="cart-img" style={{ flex: "0 0 65px", maxWidth: "65px" }}>
                                    <Link to="javascript:void(0)">
                                      <img
                                        src={
                                          item.color?.image_url?.split("@")[0]
                                        }
                                        className="w-100 h-100"
                                        loading="lazy"
                                        alt="product-img"
                                      />
                                    </Link>
                                  </div>
                                  <div className="cart-info" style={{ flex: "1", paddingLeft: "12px", minWidth: 0 }}>
                                    <h4 style={{ fontSize: "12px", fontWeight: "600", marginBottom: "4px", lineHeight: "1.3", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                      <Link to="javascript:void(0)">
                                        {item.product.product_name} (
                                        {item.color?.color.split(",")[0]})
                                      </Link>
                                    </h4>
                                    <div className="d-flex justify-content-between align-items-center" style={{ fontSize: "11px", color: "#666" }}>
                                      <div className="size">
                                        Size: {item?.color?.size.name}
                                      </div>
                                      {item.color?.quantity > 0 ? (
                                        <div className="size">
                                          Qty.: {item?.quantity}
                                        </div>
                                      ) : (
                                        <div className="size">
                                          <strong style={{ color: "red", fontSize: "11px" }}>
                                            Out of Stock
                                          </strong>
                                        </div>
                                      )}
                                      {item.color?.quantity > 0 ? (
                                        <div className="size">
                                          {currencySymbol === "₹" ? "₹" : "$"}
                                          {(
                                            item.color?.price * 1
                                          ).toLocaleString("en-IN", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}
                                        </div>
                                      ) : null}
                                    </div>
                                    {item.color?.price &&
                                    item.color?.quantity > 0 ? (
                                      <span className="mt-0" style={{ fontSize: "13px", fontWeight: "600", color: "#175d31", display: "block", marginTop: "4px" }}>
                                        {currencySymbol === "₹" ? "₹" : "$"}
                                        {(
                                          item.color?.price * item?.quantity
                                        ).toLocaleString("en-IN", {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}
                                      </span>
                                    ) : null}
                                  </div>

                                  <div
                                    className="del-icon"
                                    style={{ flex: "0 0 24px", textAlign: "right", marginLeft: "5px" }}
                                    onClick={() =>
                                      handleItemDelete(item.cart_item_id)
                                    }
                                  >
                                    <i
                                      className="fa fa-trash"
                                      style={{ color: "#175d31", fontSize: "16px" }}
                                    ></i>
                                  </div>
                                </li>
                              ) : null
                            )}
                            {cartId
                              ? null
                              : preLoginCount > 0
                              ? preLoginCart.map((item, i) => (
                                  <li key={i} className="d-flex align-items-center mb-3 pb-2" style={{ borderBottom: "1px solid #f5f5f5" }}>
                                    <div className="cart-img" style={{ flex: "0 0 65px", maxWidth: "65px" }}>
                                      <Link to={`/product-details/${item.product?.slug || item.product?.id || item.product_id}`}>
                                        <img
                                          src={item.image_url?.split("@")[0]}
                                          className="w-100 h-100"
                                          loading="lazy"
                                          alt="product-img"
                                        />
                                      </Link>
                                    </div>
                                    <div className="cart-info" style={{ flex: "1", paddingLeft: "12px", minWidth: 0 }}>
                                      <h4 style={{ fontSize: "12px", fontWeight: "600", marginBottom: "4px", lineHeight: "1.3", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        <Link to={`/product-details/${item.product?.slug || item.product?.id || item.product_id}`}>
                                          {item.product_name} (
                                          {item.color.split(",")[0]})
                                        </Link>
                                      </h4>
                                      <div className="d-flex justify-content-between align-items-center" style={{ fontSize: "11px", color: "#666" }}>
                                        <div className="size">
                                          Size: {item.size}
                                        </div>

                                        <div className="size">
                                          Qty.: {item?.quantity}
                                        </div>

                                        <div className="size">
                                          {currencySymbol === "₹" ? "₹" : "$"}
                                          {(item.price * 1).toLocaleString(
                                            "en-IN",
                                            {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            }
                                          )}
                                        </div>
                                      </div>
                                      <span className="mt-0" style={{ fontSize: "13px", fontWeight: "600", color: "#175d31", display: "block", marginTop: "4px" }}>
                                        {currencySymbol === "₹" ? "₹" : "$"}
                                        {(
                                          item.price * item?.quantity
                                        ).toLocaleString("en-IN", {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}
                                      </span>
                                    </div>
                                    <div
                                      className="del-icon"
                                      style={{ flex: "0 0 24px", textAlign: "right", marginLeft: "5px" }}
                                      onClick={() =>
                                        removePreLoginCartItemhandle(
                                          item.color_id
                                        )
                                      }
                                    >
                                      <i
                                        className="fa fa-trash"
                                        style={{ color: "#175d31", fontSize: "16px" }}
                                      ></i>
                                    </div>
                                  </li>
                                ))
                              : null}
                            {cartCount > 0 || preLoginCount > 0 ? (
                              <li className="checkout-btn sticky-btn">
                                {cartItemList ? (
                                  <Link
                                    to={`/cart`}
                                    onClick={() => handleViewCart()}
                                    style={{ backgroundColor: "#175d31" }}
                                  >
                                    View Cart
                                  </Link>
                                ) : (
                                  <Link
                                    to="javascript:void(0)"
                                    onClick={() => {
                                      handleViewCart();
                                      handleLogin();
                                    }}
                                    style={{ backgroundColor: "#175d31" }}
                                  >
                                    View Cart
                                  </Link>
                                )}
                              </li>
                            ) : (
                              <li>
                                <strong>Your cart has no items to show!</strong>
                                <button
                                  type="button"
                                  onClick={toggleCart}
                                  className="close"
                                  style={{
                                    fontSize: "1.5rem",
                                    opacity: "0.7",
                                    position: "absolute",
                                    top: "0",
                                    right: "0",
                                    fontWeight: "600",
                                  }}
                                >
                                  &times;
                                </button>
                              </li>
                            )}
                          </ul>
                        )}
                      </div>
                  </div>
                </div>

                <div className="d-flex align-items-center justify-content-center flex-grow-1 flex-shrink-1" style={{ order: 1, margin: 0, flexWrap: "nowrap", whiteSpace: "nowrap", minWidth: 0, width: "100%" }}>
                  <div className="position-static main-menu-web" style={{ padding: "0 10px", width: "100%" }}>
                    <div className="main-header-inner" style={{ width: "100%" }}>
                      <div className="main-menu" style={{ width: "100%" }}>
                        <nav id="mobile-menu" style={{ fontSize: "14.5px", width: "100%" }}>
                          <ul className="d-flex align-items-center m-0 p-0 w-100" style={{ justifyContent: "space-evenly", flexWrap: "nowrap", whiteSpace: "nowrap" }}>
                            <li>
                              <Link to={`/`} className="font-size">
                                Home
                              </Link>
                            </li>
                            <li>
                              <Link to={`/collections`} className="font-size">
                                Collections
                              </Link>
                            </li>
                            <li>
                              <Link to="/mens">
                                Men's <i className="fa fa-angle-down"></i>
                              </Link>
                              {menSubCategory && (
                                <ul className="dropdown compact-dropdown">
                                  {menSubCategory?.map((item, i) => (
                                    <li key={i} className="d-flex align-items-center mb-3 pb-2" style={{ borderBottom: "1px solid #f5f5f5" }}>
                                      <Link to="/mens" state={{ initialType: item.category.includes("Polo") ? "Polo Neck" : "Round Neck" }}>
                                        {item.category}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </li>
                            <li>
                              <Link to="/womens">
                                Women's <i className="fa fa-angle-down"></i>
                              </Link>
                              {womenSubCategory && (
                                <ul className="dropdown compact-dropdown">
                                  {womenSubCategory?.map((item, i) => (
                                    <li key={i} className="d-flex align-items-center mb-3 pb-2" style={{ borderBottom: "1px solid #f5f5f5" }}>
                                      <Link to="/womens" state={{ initialType: item.category.includes("Polo") ? "Polo Neck" : "Round Neck" }}>
                                        {item.category}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </li>
                            <li>
                              <Link to={`/sustainability`}>Sustainability</Link>
                            </li>
                            {token && hasOrders && (
                              <li>
                                <Link to={`/track-order`}>Track Your Order</Link>
                              </li>
                            )}
                            <li>
                              <Link to={`/contact-us`}>Contact Us</Link>
                            </li>

                            <li>
                              <Link to={`/about-us`}>About Us</Link>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2 ml-auto position-static d-none">
                    <div className="header-setting-option setting-style-2">
                      <form onSubmit={handleSearchSubmit} className="react-search-box mobile-search me-1 ms-1 search-wrap d-flex dropdown" style={{ width: "67%" }}>
                          <button type="submit" className="search-trigger me-1">
                            <i className="ion-ios-search-strong"></i>
                          </button>
                          <input
                            type="text"
                            placeholder="Search..."
                            className="form-control transparent-background"
                            value={inputValue}
                            onChange={(e) => handleSearchChange(e)}
                            maxLength={50}
                          />
                          {inputValue ? (
                            <button
                              type="button"
                              className="search-trigger me-1"
                              onClick={() => handleSearchClose()}
                            >
                              <i className="ion-ios-close"></i>
                            </button>
                          ) : null}
                          {searchBoxVisible ? (
                            <div
                              className="dropdown-menu width60"
                              style={{
                                display: "block",
                              }}
                            >
                              <ul>
                                {filteredSuggestions?.length > 0 ? (
                                  filteredSuggestions.map(
                                    (suggestion, index) => (
                                      <li
                                        key={index}
                                        onClick={() =>
                                          handleSearchProductSelect(suggestion)
                                        }
                                        className="d-flex align-items-center dropdown-item"
                                        style={{ padding: "10px", cursor: "pointer", borderBottom: "1px solid #f0f0f0", whiteSpace: "normal" }}
                                      >
                                      <img
                                        src={suggestion.productUrl || logo || `${PATH}/assets/img/bamboosa/bamboosa-logo.png`}
                                        alt="Product-Img"
                                        loading="lazy"
                                        className="me-3"
                                        style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px" }}
                                        onError={(e) => { e.target.onerror = null; e.target.src = logo || `${PATH}/assets/img/bamboosa/bamboosa-logo.png`; }}
                                      />
                                      <span style={{ fontSize: "14px", fontWeight: "500", color: "#333", lineHeight: "1.4" }}>{suggestion.productName}</span>
                                    </li>
                                  )
                                )
                              ) : (
                                <li style={{ padding: "10px", textAlign: "center", color: "#666", fontSize: "14px" }}>
                                  No products found.
                                </li>
                              )}
                            </ul>
                            </div>
                          ) : null}
                      </form>
                    </div>
                  </div>
                                  </div>
              </div>

              <div className="mobile-menu ms-1 mt-0 me-5">
                      <style>
                        {`
                          .custom-mobile-drawer {
                            position: fixed;
                            top: 0;
                            left: -100%;
                            width: 85%;
                            max-width: 400px;
                            height: 100vh;
                            background-color: #fff;
                            z-index: 10000;
                            transition: left 0.3s ease-in-out;
                            overflow-y: auto;
                            box-shadow: 2px 0 15px rgba(0,0,0,0.1);
                            padding-bottom: 30px;
                          }
                          .custom-mobile-drawer.open {
                            left: 0;
                          }
                          .drawer-overlay {
                            position: fixed;
                            top: 0;
                            left: 0;
                            width: 100vw;
                            height: 100vh;
                            background: rgba(0,0,0,0.5);
                            z-index: 9999;
                            display: none;
                          }
                          .drawer-overlay.open {
                            display: block;
                            touch-action: none;
                            -webkit-overflow-scrolling: none;
                          }
                          .drawer-header {
                            display: flex;
                            align-items: center;
                            padding: 20px;
                            background-color: #f9f9f9;
                            border-bottom: 1px solid #eee;
                          }
                          .drawer-header .avatar {
                            width: 45px;
                            height: 45px;
                            border-radius: 50%;
                            background-color: #e0e0e0;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-size: 20px;
                            color: #555;
                            margin-right: 15px;
                          }
                          .drawer-header .greeting {
                            font-weight: 700;
                            font-size: 16px;
                            color: #111;
                            margin-bottom: 2px;
                          }
                          .drawer-header .login-link {
                            font-size: 14px;
                            color: #007bff;
                            text-decoration: none;
                          }
                          .drawer-section {
                            padding: 20px 0 10px 0;
                          }
                          .drawer-section-title {
                            font-size: 12px;
                            font-weight: 600;
                            color: #777;
                            margin-left: 20px;
                            margin-bottom: 10px;
                            letter-spacing: 1px;
                          }
                          .drawer-menu {
                            list-style: none;
                            padding: 0;
                            margin: 0;
                          }
                          .drawer-menu li {
                            position: relative;
                          }
                          .drawer-menu li a {
                            display: flex;
                            align-items: center;
                            padding: 12px 20px;
                            color: #222;
                            font-size: 15px;
                            font-weight: 500;
                            text-decoration: none;
                            transition: background 0.2s;
                          }
                          .drawer-menu li a:hover {
                            background-color: #f5f5f5;
                          }
                          .drawer-menu li a i {
                            font-size: 18px;
                            width: 30px;
                            text-align: center;
                            margin-right: 15px;
                            color: #555;
                          }
                          .drawer-close {
                            position: absolute;
                            top: 15px;
                            right: 15px;
                            font-size: 24px;
                            color: #888;
                            cursor: pointer;
                            background: none;
                            border: none;
                          }
                          .drawer-submenu {
                            list-style: none;
                            padding: 0;
                            margin: 0;
                            background-color: #f9f9f9;
                            border-left: 3px solid #0b2e13;
                          }
                          .drawer-submenu li a {
                            padding: 10px 20px 10px 50px;
                            font-size: 14px;
                            color: #444;
                          }
                          .drawer-expand-btn {
                            position: absolute;
                            right: 10px;
                            top: 5px;
                            width: 30px;
                            height: 30px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-size: 18px;
                            color: #555;
                            cursor: pointer;
                            z-index: 10;
                          }
                        `}
                      </style>

                      <div className={`drawer-overlay ${mobileMenuVisible ? 'open' : ''}`} onClick={() => handleMobileMenu()}></div>
                      
                      <div className={`custom-mobile-drawer ${mobileMenuVisible ? 'open' : ''}`}>
                        <button className="drawer-close" onClick={() => handleMobileMenu()}>&times;</button>
                        
                        <div className="drawer-header">
                          <div className="avatar">
                            <i className="fa fa-user"></i>
                          </div>
                          <div>
                            {userData && !Array.isArray(userData) && userData.id ? (
                              <>
                                <div className="greeting">Hey There!</div>
                                <Link to="/my-account" className="login-link" onClick={() => handleMobileMenu()}>My Account</Link>
                              </>
                            ) : (
                              <>
                                <div className="greeting">Hey There!</div>
                                <div onClick={() => { handleMobileMenu(); setShowAuthModal(true); }} className="login-link" style={{ cursor: "pointer" }}>Sign In / Sign Up</div>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="drawer-section">
                          <div className="drawer-section-title">SHOP IN</div>
                          <ul className="drawer-menu">
                            <li>
                              <Link to="/" onClick={() => handleMobileMenu()}>
                                <i className="fa fa-home"></i> Home
                              </Link>
                            </li>
                            <li>
                              <Link to="/collections" onClick={() => handleMobileMenu()}>
                                <i className="fa fa-th-large"></i> Collections
                              </Link>
                            </li>
                            <li>
                              <Link to="/mens" onClick={() => handleMobileMenu()}>
                                <i className="fa fa-male"></i> Men's
                              </Link>
                              {menSubCategory && (
                                <>
                                  <div className="drawer-expand-btn" onClick={(e) => { e.preventDefault(); handleMenMenu(); }}>
                                    {menMenuVisible ? '-' : '+'}
                                  </div>
                                  <ul className={`drawer-submenu ${menMenuVisible ? 'd-block' : 'd-none'}`}>
                                    {menSubCategory?.map((item, i) => (
                                      <li key={i} className="d-flex align-items-center mb-3 pb-2" style={{ borderBottom: "1px solid #f5f5f5" }}>
                                        <Link to="/mens" state={{ initialType: item.category.includes("Polo") ? "Polo Neck" : "Round Neck" }} onClick={() => handleMobileMenu()}>
                                          {item.category}
                                        </Link>
                                      </li>
                                    ))}
                                  </ul>
                                </>
                              )}
                            </li>
                            <li>
                              <Link to="/womens" onClick={() => handleMobileMenu()}>
                                <i className="fa fa-female"></i> Women's
                              </Link>
                              {womenSubCategory && (
                                <>
                                  <div className="drawer-expand-btn" onClick={(e) => { e.preventDefault(); handleWomenMenu(); }}>
                                    {womenMenuVisible ? '-' : '+'}
                                  </div>
                                  <ul className={`drawer-submenu ${womenMenuVisible ? 'd-block' : 'd-none'}`}>
                                    {womenSubCategory?.map((item, i) => (
                                      <li key={i} className="d-flex align-items-center mb-3 pb-2" style={{ borderBottom: "1px solid #f5f5f5" }}>
                                        <Link to="/womens" state={{ initialType: item.category.includes("Polo") ? "Polo Neck" : "Round Neck" }} onClick={() => handleMobileMenu()}>
                                          {item.category}
                                        </Link>
                                      </li>
                                    ))}
                                  </ul>
                                </>
                              )}
                            </li>
                          </ul>
                        </div>
                        
                        <hr style={{ margin: "0 20px", borderTop: "1px solid #eee" }} />

                        <div className="drawer-section">
                          <div className="drawer-section-title">ENGAGE</div>
                          <ul className="drawer-menu">
                            <li>
                              <Link to="/sustainability" onClick={() => handleMobileMenu()}>
                                <i className="fa fa-leaf"></i> Sustainability
                              </Link>
                            </li>
                            {token && hasOrders && (
                              <li>
                                <Link to="/track-order" onClick={() => handleMobileMenu()}>
                                  <i className="fa fa-truck"></i> Track Your Order
                                </Link>
                              </li>
                            )}
                            <li>
                              <Link to="/contact-us" onClick={() => handleMobileMenu()}>
                                <i className="fa fa-envelope"></i> Contact Us
                              </Link>
                            </li>
                            <li>
                              <Link to="/about-us" onClick={() => handleMobileMenu()}>
                                <i className="fa fa-info-circle"></i> About Us
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>

              </div>
            </div>
          </div>
        </div>

        <div className="box-search-content search_active block-bg close__top">
          <form className="minisearch" action="#">
            <div className="field__search">
              <input type="text" placeholder="Search Our Catalog" />
              <div className="action">
                <Link to="/search" aria-label="Search Catalog">
                  <i className="fa fa-search"></i>
                </Link>
              </div>
            </div>
          </form>
          <div className="close__wrap">
            <span>close</span>
          </div>
        </div>
      </header>

      <main id="main-content">
        <Outlet />
      </main>

      <footer style={{ backgroundColor: "#f7f7f7", color: "#333", padding: "60px 40px 30px 40px" }}>
        <div style={{ maxWidth: "1300px", margin: "0 auto" }}>
            <div className="d-flex flex-column flex-lg-row justify-content-between flex-wrap" style={{ gap: "30px 20px" }}>
               
               <div className="mb-4 mb-lg-0" style={{ flex: "0 0 auto", maxWidth: "280px" }}>
                   <h2 style={{ fontWeight: "400", fontSize: "16px", color: "#333", letterSpacing: "1px", marginBottom: "15px", textTransform: "uppercase" }}>KEEP IN TOUCH</h2>
                   
                   <div className="d-flex mb-4">
                     <a aria-label="Facebook" href="https://www.facebook.com/people/Bamboosa-Pvt-Ltd/61559649682837/?is_tour_dismissed" target="_blank" rel="noreferrer" style={{ width: "40px", height: "40px", borderRadius: "50%", backgroundColor: "#e6e6e6", display: "flex", alignItems: "center", justifyContent: "center", color: "#111", marginRight: "15px", textDecoration: "none", transition: "all 0.3s ease" }}>
                       <i className="fa fa-facebook" style={{ fontSize: "16px" }}></i>
                       <span className="visually-hidden">Facebook</span>
                     </a>
                     <a aria-label="Instagram" href="https://www.instagram.com/bamboosalifestyle/?next=%2F" target="_blank" rel="noreferrer" style={{ width: "40px", height: "40px", borderRadius: "50%", backgroundColor: "#e6e6e6", display: "flex", alignItems: "center", justifyContent: "center", color: "#111", marginRight: "15px", textDecoration: "none", transition: "all 0.3s ease" }}>
                       <i className="fa fa-instagram" style={{ fontSize: "16px" }}></i>
                       <span className="visually-hidden">Instagram</span>
                     </a>
                   </div>

                   <div>
                     <p style={{ color: "#595959", fontSize: "13px", marginBottom: "12px", fontWeight: "600", letterSpacing: "1px" }}>EXPERIENCE BAMBOOSA APP ON MOBILE</p>
                     <div className="d-flex mb-4">
                        <a aria-label="Google Play Store" href="https://play.google.com/store/apps/details?id=com.bamboosa" target="_blank" rel="noreferrer" className="me-2">
                           <img width="135" height="40" src={`${PATH}/assets/img/bamboosa/play_badge.png`} style={{ height: "40px", objectFit: "contain" }} alt="Google Play" />
                        </a>
                        <a aria-label="Apple App Store" href="https://apps.apple.com/in/app/bamboosa/id6502903278" target="_blank" rel="noreferrer">
                           <img width="135" height="40" src={`${PATH}/assets/img/bamboosa/app_store.png`} style={{ height: "40px", objectFit: "contain" }} alt="App Store" />
                        </a>
                     </div>
                   </div>

                   <div className="mt-4 pt-2">
                     <div className="d-flex align-items-center mb-4">
                        <div style={{ marginRight: "15px", width: "48px", textAlign: "center" }}>
                          <img width="48" height="48" src="https://constant.myntassets.com/web/assets/img/6c3306ca-1efa-4a27-8769-3b69d16948741574602902452-original.png" alt="Original" style={{ width: "48px" }} />
                        </div>
                        <div style={{ lineHeight: "1.4" }}>
                         <span style={{ fontWeight: "600", color: "#282c3f", fontSize: "15px" }}>100% ORIGINAL</span> 
                         <span style={{ color: "#555866", fontSize: "15px" }}> guarantee<br/>for all products at bamboosa.online</span>
                       </div>
                     </div>
                     <div className="d-flex align-items-center">
                       <div style={{ marginRight: "15px", width: "48px", textAlign: "center", display: "flex", justifyContent: "center" }}>
                         <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#535766" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                           <path d="M2.5 2v6h6M21.5 22v-6h-6" />
                           <path d="M22 11.5A10 10 0 0 0 3.2 7.2M2 12.5a10 10 0 0 0 18.8 4.2" />
                           <text x="12" y="16.5" fontSize="10" textAnchor="middle" fontWeight="bold" stroke="none" fill="#535766">7</text>
                         </svg>
                       </div>
                       <div style={{ lineHeight: "1.4" }}>
                         <span style={{ fontWeight: "600", color: "#333", fontSize: "15px" }}>Return within 7 days</span> 
                         <span style={{ color: "#666", fontSize: "15px" }}> of<br/>receiving your order</span>
                       </div>
                     </div>
                   </div>
               </div>

               <div className="mb-4 mb-lg-0 pt-lg-2" style={{ flex: "0 0 auto", maxWidth: "360px" }}>
                   <h3 style={{ fontWeight: "600", fontSize: "13px", color: "#333", letterSpacing: "1px", marginBottom: "25px", textTransform: "uppercase" }}>Address</h3>
                   <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    <li style={{ color: "#666", fontSize: "14px", marginBottom: "15px", lineHeight: "1.6" }}>
                      {businessName || 'BambooSA Agro Innovations (OPC) Private Limited.'}
                    </li>
                    <li style={{ color: "#666", fontSize: "14px", marginBottom: "15px", lineHeight: "1.6" }}>
                      {address ? (
                        <>{address},<br />{city} {zip}</>
                      ) : (
                        <>Office no. 505, City Avenue,<br />Oppo. Phoenix Mall of the Millennium,<br />Mumbai-Bangalore Highway, Wakad,<br />Pune 411057</>
                      )}
                    </li>
                    <li style={{ color: "#666", fontSize: "14px", marginBottom: "15px" }}>
                      <a href={`tel:${phone ? (phone.startsWith('+91') ? phone : `+91${phone}`) : '+919975536173'}`} style={{ color: "#666", textDecoration: "none" }}>Talk to us: {phone ? (phone.startsWith('+91') ? phone : `+91-${phone}`) : '+91-9975536173'}</a>
                    </li>
                    <li style={{ color: "#666", fontSize: "14px", marginBottom: "15px" }}>
                      <a href={`mailto:${email || 'customercare@bamboosa.online'}`} style={{ color: "#666", textDecoration: "none" }}>{email || 'customercare@bamboosa.online'}</a>
                    </li>
                   </ul>
               </div>

               <div className="mb-4 mb-lg-0 pt-lg-2" style={{ flex: "0 0 auto", width: "170px" }}>
                   <h3 style={{ fontWeight: "600", fontSize: "13px", color: "#333", letterSpacing: "1px", marginBottom: "25px", textTransform: "uppercase" }}>Shop</h3>
                   <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                     <li style={{ marginBottom: "15px" }}><Link to="/womens" state={{ initialType: "Polo Neck" }} style={{ color: "#666", fontSize: "14px", textDecoration: "none" }}>Women's Polo Neck</Link></li>
                     <li style={{ marginBottom: "15px" }}><Link to="/womens" state={{ initialType: "Round Neck" }} style={{ color: "#666", fontSize: "14px", textDecoration: "none" }}>Women's Round Neck</Link></li>
                     <li style={{ marginBottom: "15px" }}><Link to="/mens" state={{ initialType: "Polo Neck" }} style={{ color: "#666", fontSize: "14px", textDecoration: "none" }}>Men's Polo Neck</Link></li>
                     <li style={{ marginBottom: "15px" }}><Link to="/mens" state={{ initialType: "Round Neck" }} style={{ color: "#666", fontSize: "14px", textDecoration: "none" }}>Men's Round Neck</Link></li>
                   </ul>
               </div>

               <div className="mb-4 mb-lg-0 pt-lg-2" style={{ flex: "0 0 auto", width: "180px" }}>
                   <h3 style={{ fontWeight: "600", fontSize: "13px", color: "#333", letterSpacing: "1px", marginBottom: "25px", textTransform: "uppercase" }}>More Info</h3>
                   <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                     <li style={{ marginBottom: "15px" }}><Link to="/" style={{ color: "#666", fontSize: "14px", textDecoration: "none" }}>Home</Link></li>
                     <li style={{ marginBottom: "15px" }}><Link to="/about-us" style={{ color: "#666", fontSize: "14px", textDecoration: "none" }}>About Us</Link></li>
                     <li style={{ marginBottom: "15px" }}><Link to="/contact-us" style={{ color: "#666", fontSize: "14px", textDecoration: "none" }}>Contact Us</Link></li>
                     <li style={{ marginBottom: "15px" }}><Link to="/privacy-policy" style={{ color: "#666", fontSize: "14px", textDecoration: "none" }}>Privacy Policy</Link></li>
                     <li style={{ marginBottom: "15px" }}><Link to="/terms-and-conditions" style={{ color: "#666", fontSize: "14px", textDecoration: "none" }}>Terms & Conditions</Link></li>
                     <li style={{ marginBottom: "15px" }}><Link to="/return-policy" style={{ color: "#666", fontSize: "14px", textDecoration: "none" }}>Return & Refund Policy</Link></li>
                     <li style={{ marginBottom: "15px" }}><Link to="/sustainability" style={{ color: "#666", fontSize: "14px", textDecoration: "none" }}>Sustainability</Link></li>
                   </ul>
               </div>
            </div>

            <div className="row mt-4 pt-3">
              <div className="col-12 d-flex justify-content-center align-items-center">
                 <div style={{ color: "#333", fontSize: "13px" }}>
                    Copyright © 2026 BambooSA Agro Innovations (OPC) Private Limited. All Rights Reserved
                 </div>
              </div>
            </div>

        </div>
      </footer>
      {coupons?.length > 0 && isCouponValid ? (
        <FloatingCoupon coupon={coupons[0]} />
      ) : null}
      <WhatsAppButton />
      <FloatingBackButton />
      <WelcomeAuthModal show={showAuthModal} onHide={() => {
        setShowAuthModal(false);
        setAuthModalMessage('');
      }} customMessage={authModalMessage} />
    </div>
  );
}
